import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Search, UserPlus } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AppClientFormDialog, type ClientRow } from '@/components/app-client-form-dialog';

interface Props {
  /** id del cliente seleccionado (para marcar el check). */
  value: string | null;
  /** Texto a mostrar en el trigger del cliente elegido (el picker no siempre
   * tiene el objeto en sus resultados filtrados). */
  selectedLabel?: string;
  onChange: (client: ClientRow | null) => void;
}

/**
 * Selector de cliente con BÚSQUEDA server-side (escala a miles): combobox
 * (Popover + Input) que consulta `/api/app/clients?q=` con debounce. Permite
 * crear un cliente al vuelo. Devuelve el objeto cliente completo.
 */
export function AppClientPicker({ value, selectedLabel, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isFetching } = useQuery({
    queryKey: ['app-clients', q],
    queryFn: () =>
      api<{ items: ClientRow[] }>(`/api/app/clients${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    enabled: open, // solo busca con el popover abierto
  });
  const items = data?.items ?? [];

  return (
    <div className="flex items-end gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="flex-1 justify-between font-normal"
          >
            <span className={cn('truncate', !selectedLabel && 'text-muted-foreground')}>
              {selectedLabel || 'Elegí un cliente…'}
            </span>
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
          <div className="relative border-b border-border">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o RUC…"
              className="border-0 pl-9 shadow-none focus-visible:ring-0"
            />
          </div>
          <div className="max-h-64 overflow-auto p-1">
            {isFetching && items.length === 0 ? (
              <div className="px-2 py-3 text-sm text-muted-foreground">Buscando…</div>
            ) : items.length === 0 ? (
              <div className="px-2 py-3 text-sm text-muted-foreground">Sin resultados.</div>
            ) : (
              items.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    onChange(c);
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                >
                  <Check
                    className={cn('size-4 shrink-0', value === c.id ? 'opacity-100' : 'opacity-0')}
                  />
                  <span className="min-w-0 flex-1 truncate">
                    {c.razonSocial || c.name}
                    {c.ruc ? <span className="text-muted-foreground"> · {c.ruc}</span> : null}
                  </span>
                </button>
              ))
            )}
          </div>
          <div className="border-t border-border p-1">
            <Button
              type="button"
              variant="ghost"
              className="w-full justify-start"
              onClick={() => {
                setOpen(false);
                setFormOpen(true);
              }}
            >
              <UserPlus className="size-4" /> Nuevo cliente
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <AppClientFormDialog open={formOpen} onOpenChange={setFormOpen} onSaved={(client) => onChange(client)} />
    </div>
  );
}
